import React, { useState } from 'react';
import { Select, DatePicker, Button, Space, Tooltip, Typography } from 'antd';
import { CalendarOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { analyticsWindowLabel } from '@/utils/analyticsWindow';

const { RangePicker } = DatePicker;

export type AnalyticsPeriod =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_90_days'
  | 'all'
  | 'custom';

export interface AnalyticsDateRange {
  period: AnalyticsPeriod;
  customRange?: [Dayjs, Dayjs];
}

/** The range of the data on screen and when it was fetched (ms since epoch). */
export interface AnalyticsShownRange {
  range: AnalyticsDateRange;
  asOf: number;
}

interface Props {
  /** Currently-applied range (passed back from the parent after Search). */
  value: AnalyticsDateRange;
  /** Fires only when the user clicks Search. Auto-fire on change was
   *  removed per §6.4 — users complained the page would re-query on
   *  every keystroke when picking a custom range. */
  onApply: (next: AnalyticsDateRange) => void;
  /** Optional disabled state while a query is in flight. */
  loading?: boolean;
  /** What the data on screen covers: the range of the last fetch that
   *  succeeded and when it started. The label describes it, so it stays true
   *  while a new search is loading, after one fails, and on a page left open
   *  past midnight. null: nothing loaded yet, no label. Omitted: the label
   *  describes `value` as of now. */
  shown?: AnalyticsShownRange | null;
}

/**
 * §6.4 — Shared period selector + RangePicker + Search button used by every
 * post-launch analytics page that takes a date range. Keeps state local
 * until the user clicks Search, so partial selections never hit the API.
 */
export const AnalyticsDateRangeFilter: React.FC<Props> = ({ value, onApply, loading, shown }) => {
  const [pendingPeriod, setPendingPeriod] = useState<AnalyticsPeriod>(value.period);
  const [pendingRange, setPendingRange] = useState<[Dayjs, Dayjs] | undefined>(value.customRange);
  const windowLabel =
    shown === undefined
      ? analyticsWindowLabel(value)
      : shown && analyticsWindowLabel(shown.range, shown.asOf);

  const submit = () => {
    if (pendingPeriod === 'custom') {
      if (!pendingRange || !pendingRange[0] || !pendingRange[1]) return;
      onApply({ period: 'custom', customRange: pendingRange });
    } else {
      onApply({ period: pendingPeriod });
    }
  };

  return (
    <Space wrap>
      <Select
        value={pendingPeriod}
        onChange={(v) => setPendingPeriod(v as AnalyticsPeriod)}
        style={{ width: 160 }}
        options={[
          { value: 'today', label: 'Today' },
          { value: 'yesterday', label: 'Yesterday' },
          { value: 'last_7_days', label: 'Last 7 days' },
          { value: 'last_30_days', label: 'Last 30 days' },
          { value: 'last_90_days', label: 'Last 90 days' },
          { value: 'all', label: 'All time' },
          { value: 'custom', label: 'Custom range' },
        ]}
      />
      {pendingPeriod === 'custom' && (
        <RangePicker
          value={pendingRange}
          onChange={(r) => {
            if (r && r[0] && r[1]) setPendingRange([r[0], r[1]]);
            else setPendingRange(undefined);
          }}
          disabledDate={(d) => d.isAfter(dayjs())}
        />
      )}
      <Button
        type="primary"
        icon={<SearchOutlined />}
        onClick={submit}
        loading={loading}
        disabled={
          pendingPeriod === 'custom' && (!pendingRange || !pendingRange[0] || !pendingRange[1])
        }
      >
        Search
      </Button>
      {windowLabel && (
        <Tooltip
          trigger={['hover', 'focus']}
          title="Figures that use this filter cover this window, in IST (UTC+05:30). Both ends are included."
        >
          <Typography.Text
            type="secondary"
            tabIndex={0}
            role="note"
            aria-label={`Figures that use this filter cover ${windowLabel}, both ends included`}
            style={{ whiteSpace: 'nowrap' }}
          >
            <CalendarOutlined /> {windowLabel}
          </Typography.Text>
        </Tooltip>
      )}
    </Space>
  );
};
